import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import Table from "react-bootstrap/Table";
import TableFilter from "./TableFilter";
import Pagination from "../pagination/Pagination";
import { capitalize } from "../../../helpers/Utils";
import { SortSvg } from "../../../svgFiles/SortSvg";
import { useSelector } from "react-redux";

function getNestedValue(obj, accessor, capi) {
  if (!accessor) return "";

  let value = accessor.split(".").reduce((acc, key) => {
    if (acc == null) return "";
    return acc[key] !== undefined && acc[key] !== null ? acc[key] : "";
  }, obj);

  if (value && capi) {
    value = capitalize(value);
  }

  return value;
}

function isEmptySortValue(value) {
  return value === null || value === undefined || value === "";
}

/** Compare two cell values for table sorting (asc = a before b). */
function compareSortValues(aVal, bVal) {
  const aEmpty = isEmptySortValue(aVal);
  const bEmpty = isEmptySortValue(bVal);
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1; // empties last
  if (bEmpty) return -1;

  if (typeof aVal === "number" && typeof bVal === "number") {
    return aVal - bVal;
  }

  const aStr = String(aVal).trim();
  const bStr = String(bVal).trim();

  if (/^-?\d+(\.\d+)?$/.test(aStr) && /^-?\d+(\.\d+)?$/.test(bStr)) {
    return Number(aStr) - Number(bStr);
  }

  return aStr.localeCompare(bStr, undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function columnKey(column, index) {
  return column?.accessor != null ? String(column.accessor) : `col-${index}`;
}

const Datatable = ({
  tableData,
  columns,
  title,
  onPageChange,
  page,
  totalPages,
  showPegination = true,
  showFilter = {},
  isLoading = false,
  buttonTitle,
  onClick,
  onFilterSearch,
  dropdownItems,
  onDropdownSelect,
  dropdownView,
  handleRowClick,
  className = "",
  resizableColumns = false,
  loadMoreMode = false,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
}) => {
  const highlightedIds = useSelector(
    (state) => state.filters.highlightedIds || []
  );

  const [itemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [columnWidths, setColumnWidths] = useState({});
  const tableRef = useRef(null);
  const tableScrollRef = useRef(null);
  const resizingRef = useRef(null);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    if (!loadMoreMode || !hasMore || isLoadingMore || !loadMoreRef.current) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) onLoadMore?.(); },
      { rootMargin: "180px 0px" }
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [loadMoreMode, hasMore, isLoadingMore, onLoadMore]);

  const totalItems = tableData?.pagination?.total ?? 0;

  const sortedData = useMemo(() => {
    const orders = tableData?.orders;
    if (!Array.isArray(orders)) return [];

    const sortableData = [...orders];
    if (!sortConfig.key) return sortableData;

    const column = columns.find((col) => col.accessor === sortConfig.key);
    if (column?.sortable === false) return sortableData;

    sortableData.sort((a, b) => {
      const aVal = column?.getSortValue
        ? column.getSortValue(a)
        : getNestedValue(a, sortConfig.key);
      const bVal = column?.getSortValue
        ? column.getSortValue(b)
        : getNestedValue(b, sortConfig.key);

      const result = compareSortValues(aVal, bVal);
      return sortConfig.direction === "asc" ? result : -result;
    });

    return sortableData;
  }, [tableData, sortConfig, columns]);

  const applySort = (column, direction) => {
    if (!column?.accessor || column.sortable === false) return;
    setSortConfig({ key: column.accessor, direction });
  };

  /** Header / label click: first click asc, then toggle asc ↔ desc */
  const handleSortToggle = (column) => {
    if (!column?.accessor || column.sortable === false) return;
    const key = column.accessor;
    setSortConfig((prev) => {
      if (prev.key !== key) {
        return { key, direction: "asc" };
      }
      return {
        key,
        direction: prev.direction === "asc" ? "desc" : "asc",
      };
    });
  };

  const stopResize = useCallback(() => {
    if (!resizingRef.current) return;
    resizingRef.current = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  useEffect(() => {
    if (!resizableColumns) return undefined;

    const onMove = (event) => {
      const active = resizingRef.current;
      if (!active) return;
      const nextWidth = Math.max(48, active.startWidth + (event.clientX - active.startX));
      setColumnWidths((prev) => ({ ...prev, [active.key]: nextWidth }));
    };

    const onUp = () => stopResize();

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [resizableColumns, stopResize]);

  const handleResizeStart = (event, column, index) => {
    if (!resizableColumns || column?.resizable === false) return;
    event.preventDefault();
    event.stopPropagation();

    const table = tableRef.current;
    if (!table) return;

    const headers = Array.from(table.querySelectorAll("thead th"));
    const measured = {};
    headers.forEach((th, i) => {
      measured[columnKey(columns[i], i)] = th.getBoundingClientRect().width;
    });

    const key = columnKey(column, index);
    setColumnWidths(measured);
    resizingRef.current = {
      key,
      startX: event.clientX,
      startWidth: measured[key] || headers[index]?.getBoundingClientRect().width || 80,
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const tablePixelWidth = useMemo(() => {
    const widths = Object.values(columnWidths);
    if (!widths.length) return undefined;
    return widths.reduce((sum, width) => sum + width, 0);
  }, [columnWidths]);

  return (
    <div
      className={`${
        totalPages > 1
          ? "table_wrapper d-flex flex-column"
          : "table_wrapper d-flex flex-column singlePage"
      } ${className} ${resizableColumns ? "datatable-resizable" : ""}`.trim()}
    >
      {showFilter === true && (
        <TableFilter
          title={title}
          buttonTitle={buttonTitle}
          onClick={onClick}
          onSearch={onFilterSearch}
          dropdownItems={dropdownItems}
          onDropdownSelect={onDropdownSelect}
          dropdownView={dropdownView}
        />
      )}
      <div className="flex-grow-1 table-scroll orders-table-scroll" ref={tableScrollRef}>
        <Table
          className="mb-0"
          ref={tableRef}
          style={
            resizableColumns && tablePixelWidth
              ? {
                  width: `${tablePixelWidth}px`,
                  minWidth: `${Math.max(tablePixelWidth, 960)}px`,
                  tableLayout: "fixed",
                }
              : {
                  minWidth: "960px",
                }
          }
        >
          {highlightedIds.length === 0 && (
            <thead>
              <tr>
                {columns.map((column, index) => {
                  const canSort =
                    Boolean(column.accessor) && column.sortable !== false;
                  const isActive = sortConfig.key === column.accessor;
                  const direction = isActive ? sortConfig.direction : null;
                  const key = columnKey(column, index);
                  const width = columnWidths[key];
                  const canResize =
                    resizableColumns && column.resizable !== false;

                  return (
                    <th
                      className={`sticky-th ${column.columnClass || ""} ${
                        isActive ? "is-sorted" : ""
                      } ${canResize ? "datatable-th-resizable" : ""}`.trim()}
                      key={key}
                      onClick={() => canSort && handleSortToggle(column)}
                      style={{
                        cursor: canSort ? "pointer" : "default",
                        ...(width
                          ? { width: `${width}px`, minWidth: `${width}px` }
                          : {}),
                      }}
                      aria-sort={
                        isActive
                          ? direction === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                    >
                      <span className="d-inline-flex align-items-center gap-1 datatable-th-label">
                        {column.label}
                        {canSort && (
                          <SortSvg
                            direction={direction}
                            onAsc={() => applySort(column, "asc")}
                            onDesc={() => applySort(column, "desc")}
                          />
                        )}
                      </span>
                      {canResize && (
                        <span
                          className="datatable-col-resizer"
                          onMouseDown={(event) =>
                            handleResizeStart(event, column, index)
                          }
                          onClick={(event) => event.stopPropagation()}
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={`Resize ${typeof column.label === "string" ? column.label : "column"}`}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
          )}
          <tbody>
            {sortedData?.length > 0 ? (
              sortedData.map((row, rowIndex) => (
                <tr
                  key={
                    row.BackerID != null
                      ? String(row.BackerID)
                      : row.ShopifyId || rowIndex
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowClick?.(row?.ShopifyId, row?.ItemID);
                  }}
                  className={
                    highlightedIds.includes(row?.ShopifyItemID)
                      ? "highlighted-row"
                      : ""
                  }
                >
                  {columns.map((column, colIndex) => {
                    let cellContent;

                    if (column.cell) {
                      cellContent = column.cell({
                        row,
                        value: getNestedValue(
                          row,
                          column.accessor,
                          column.captialize || false
                        ),
                        index: rowIndex,
                      });
                    } else {
                      cellContent = getNestedValue(
                        row,
                        column.accessor,
                        column.captialize || false
                      );
                    }

                    const key = columnKey(column, colIndex);
                    const width = columnWidths[key];

                    return (
                      <td
                        className={column.columnClass || ""}
                        key={key}
                        data-label={typeof column.label === "string" ? column.label : ""}
                        data-field={column.accessor || ""}
                        onClick={(e) => colIndex === 0 && e.stopPropagation()}
                        style={
                          width
                            ? { width: `${width}px`, minWidth: `${width}px` }
                            : undefined
                        }
                      >
                        {cellContent}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  {isLoading ? "Loading..." : "No data available"}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {loadMoreMode ? (
        <div ref={loadMoreRef} className="mobile-load-more" aria-live="polite">
          {isLoadingMore ? null : hasMore ? "Pull up to load more" : "No more orders"}
        </div>
      ) : <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mt-0 w-100 px-4 pt-3 table-pagination-footer">
        <div>
          {totalItems > 0 && (
            <span>
              Showing{" "}
              <strong>
                {(page - 1) * itemsPerPage + 1} -{" "}
                {Math.min(page * itemsPerPage, totalItems)}
              </strong>{" "}
              of <strong>{totalItems}</strong>
            </span>
          )}
        </div>

        {showPegination && totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={(nextPage) => {
              tableScrollRef.current?.scrollTo({ top: 0, left: 0 });
              const mainContent = document.querySelector(
                ".admin-layout-content--shell"
              );
              if (mainContent) {
                mainContent.scrollTo({ top: 0, left: 0 });
              } else {
                window.scrollTo({ top: 0, left: 0 });
              }
              onPageChange?.(nextPage);
            }}
          />
        )}
      </div>}
    </div>
  );
};

export default Datatable;
